import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Modal, Select, Spin, Typography, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { tradeAPI } from '../../services/api';

const { Title, Text } = Typography;
const { Option } = Select;

const parseJsonSafe = (v) => {
  if (!v) return null;
  if (typeof v === 'object') return v;
  try {
    return JSON.parse(v);
  } catch (e) {
    return null;
  }
};

const getRecordItems = (rawObj) => {
  const items = rawObj?.data?.record_items;
  return Array.isArray(items) ? items : [];
};

const buildOpText = (rawObj) => {
  try {
    const items = rawObj?.data?.record_items || [];
    if (!Array.isArray(items) || items.length === 0) return '';
    return items
      .map((it) => {
        const stockName = it?.stock_name || '';
        const stockCode = it?.stock_code || '';
        const costPrice = Number(it?.cost_price) / 1000000000;
        const totalRatio = Number(it?.total_ratio) / 1000000000;
        const costText = Number.isFinite(costPrice) ? costPrice.toFixed(3) : '';
        const ratioPct = Number.isFinite(totalRatio) ? (totalRatio * 100).toFixed(2) : '';
        return `${stockName}（${stockCode}）：${costText} - ${ratioPct}%\n`;
      })
      .join('');
  } catch (e) {
    return '';
  }
};

function Field({ label, value }) {
  const v = value === null || value === undefined || value === '' ? '-' : String(value);
  return (
    <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
      <div style={{ width: 80, color: '#666', flex: '0 0 auto' }}>{label}</div>
      <div style={{ color: '#111', flex: 1, wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>{v}</div>
    </div>
  );
}

const MobileTopicRecords = () => {
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTopics = useCallback(async () => {
    try {
      const response = await tradeAPI.getTopics();
      const list = response.data || [];
      setTopics(list);
      if (!selectedTopic && list.length > 0 && list[0] && list[0].id) {
        setSelectedTopic(list[0].id);
      }
    } catch (error) {
      message.error('获取话题列表失败');
    }
  }, [selectedTopic]);

  const fetchRecords = useCallback(async () => {
    try {
      if (!selectedTopic) {
        setRecords([]);
        return;
      }
      const response = await tradeAPI.getTradeRecords(selectedTopic);
      setRecords(response.data || []);
    } catch (error) {
      message.error('获取话题记录失败');
    } finally {
      setLoading(false);
    }
  }, [selectedTopic]);

  useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

  useEffect(() => {
    setLoading(true);
    fetchRecords();
  }, [fetchRecords]);

  const handleRefresh = () => {
    fetchRecords();
  };

  const handleBack = () => {
    navigate('/', { replace: true });
  };

  const formatCreatedAt = (date) => {
    return date ? moment.utc(date).utcOffset(8).format('YYYY-MM-DD HH:mm:ss') : '-';
  };

  const getOperationRecord = (record) => {
    const obj = parseJsonSafe(record?.raw_payload);
    return buildOpText(obj) || '-';
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#f5f5f5' }}>
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '16px 16px 12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <Title level={4} style={{ margin: 0, color: '#fff', fontWeight: 600 }}>
            话题记录
          </Title>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              size="small"
              onClick={handleRefresh}
              disabled={loading}
              style={{
                background: 'rgba(255,255,255,0.2)',
                borderColor: 'rgba(255,255,255,0.3)',
                color: '#fff',
                backdropFilter: 'blur(10px)',
              }}
            >
              刷新
            </Button>
            <Button
              size="small"
              onClick={handleBack}
              style={{
                background: 'rgba(255,255,255,0.2)',
                borderColor: 'rgba(255,255,255,0.3)',
                color: '#fff',
                backdropFilter: 'blur(10px)',
              }}
            >
              返回
            </Button>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '20px 16px 16px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 50 }}>
            <Spin />
          </div>
        ) : (
          <div>
            <Card style={{ borderRadius: 10, marginBottom: 12 }} styles={{ body: { padding: 12 } }}>
              <Select placeholder="选择话题" style={{ width: '100%' }} value={selectedTopic} onChange={(v) => setSelectedTopic(v)} allowClear>
                {topics.map((t) => (
                  <Option key={t.id} value={t.id}>
                    {`${t.id} - ${t.name}`}
                  </Option>
                ))}
              </Select>
            </Card>

            {(records || []).map((record, idx) => (
              <Card key={record?.id ?? idx} style={{ borderRadius: 10, marginBottom: 12 }} styles={{ body: { padding: 12 } }}>
                <Field label="ID" value={record?.id} />
                <Field label="操作记录" value={getOperationRecord(record)} />
                <Field label="创建时间" value={formatCreatedAt(record?.created_at)} />
              </Card>
            ))}

            {!records || records.length === 0 ? (
              <Card style={{ borderRadius: 10 }} styles={{ body: { padding: 12 } }}>
                <Text type="secondary">暂无数据</Text>
              </Card>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileTopicRecords;
